import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo, useRef, useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { Empty } from "@/components/Empty/Empty.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { MarkdownWrapper } from "@/components/Markdown/MarkdownWrapper.tsx"
import { CheckmarkRegular, Icon16 } from "@/icons/Icons.ts"
import { SvgIllustration, VersionsEmpty } from "@/illustrations/Illustrations.ts"
import { copyToClipboard } from "@/lib/std.ts"
import { generateStructuredSummary } from "@/routes/tasks/TaskViewScene/generateStructuredSummary.ts"

import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

export const TaskSummaryTab = observer(({ model }: {
  model: TaskViewModel
}) => {
  const task = model.task.get()
  if (!task) return null

  const summary = useMemo(() => generateStructuredSummary(task, model), [task, model])

  if (summary.length === 0) {
    return (
      <Empty
        illustration={<VersionsEmpty className={SvgIllustration} />}
        text="Version summary will be shown here once the agent generates code"
      />
    )
  }

  return (
    <Vertical gap="24px">
      <MarkdownWrapper text={summary} />
      <CopyToClipboard text={summary} />
    </Vertical>
  )
})

const CopyToClipboard = ({ text }: { text: string }) => {
  const [showCopied, setShowCopied] = useState(false)
  const copiedTimeoutRef = useRef<number | null>(null)
  const showCopiedDuration = 2000 // in ms

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current !== null) {
        clearTimeout(copiedTimeoutRef.current)
      }
    }
  }, [])

  const handleCopy = () => {
    void copyToClipboard(text, true)
    setShowCopied(true)

    if (copiedTimeoutRef.current !== null) {
      clearTimeout(copiedTimeoutRef.current)
    }
    copiedTimeoutRef.current = window.setTimeout(() => {
      setShowCopied(false)
      copiedTimeoutRef.current = null
    }, showCopiedDuration)
  }

  return (
    <CopyToClipboardButton kind="primary" onClick={handleCopy}>
      <Horizontal alignItems="center" gap="4px">
        {showCopied && <CheckmarkRegular className={Icon16} />}
        {showCopied ? "Copied" : "Copy"}
      </Horizontal>
    </CopyToClipboardButton>
  )
}

const CopyToClipboardButton = styled(Button)`
  align-self: flex-start;
`
